/**
 * Screen Wake Lock API — impede que o monitor desligue ou o computador/celular suspenda
 * durante a partida ou em tela cheia.
 *
 * Em batalhas longas ou quando o jogador está pensando nos movimentos, a ausência
 * de cliques frequentes no navegador faz o sistema operacional ativar o protetor
 * de tela ou suspender. Este módulo segura a tela ativa e recupera a trava
 * automaticamente se o usuário minimizar e voltar para a aba.
 */

interface WakeLockSentinelLike {
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
}

let activeSentinel: WakeLockSentinelLike | null = null;
let isRequested = false;
let listenerAttached = false;

export function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

/**
 * Solicita que o sistema mantenha o display ligado.
 */
export async function acquireWakeLock(): Promise<boolean> {
  isRequested = true;
  if (!isWakeLockSupported()) return false;
  if (activeSentinel && !activeSentinel.released) return true;

  try {
    const sentinel = await (navigator as any).wakeLock.request('screen');
    activeSentinel = sentinel;
    sentinel.addEventListener('release', () => {
      if (activeSentinel === sentinel) {
        activeSentinel = null;
      }
    });

    if (!listenerAttached && typeof document !== 'undefined') {
      listenerAttached = true;
      document.addEventListener('visibilitychange', () => {
        if (isRequested && document.visibilityState === 'visible') {
          void acquireWakeLock();
        }
      });
    }

    return true;
  } catch {
    // Pode falhar silenciosamente se a bateria estiver em economia extrema ou aba sem foco
    return false;
  }
}

/**
 * Libera a trava do display.
 */
export async function releaseWakeLock(): Promise<void> {
  isRequested = false;
  if (activeSentinel) {
    try {
      await activeSentinel.release();
    } catch {
      // Ignora falha ao soltar
    }
    activeSentinel = null;
  }
}
